import type React from "react";
import { createContext, memo, useContext, useReducer } from "react";

type Dispatch = (action: Action) => void;
type ProviderProps = { children: React.ReactNode };
type State = {
  title: string;
  description: string;
  primaryAction: {
    title: string;
    onPress: () => void;
  };
  secondaryAction?: {
    title: string;
    onPress?: () => void;
  };
  visible?: boolean;
};
type Action = { type: "openModal"; payload: State } | { type: "closeModal" };

const initialDialogState = {
  title: "",
  description: "",
  visible: false,
  primaryAction: {
    title: "",
    onPress: () => {},
  },
};

const DialogContext = createContext<State | undefined>(undefined);
const DialogUpdaterContext = createContext<Dispatch | undefined>(undefined);

const dialogReducer = (state: State, action: Action) => {
  switch (action.type) {
    case "openModal": {
      return { ...state, ...action.payload, visible: true };
    }

    case "closeModal": {
      return { ...state, visible: false };
    }

    default: {
      throw new Error(`Unhandled action type: ${JSON.stringify(action)}`);
    }
  }
};

/**
 * Context exports
 */

export const DialogModalProvider = memo(({ children }: ProviderProps) => {
  const [state, dispatch] = useReducer(dialogReducer, initialDialogState);
  return (
    <DialogContext.Provider value={state}>
      <DialogUpdaterContext.Provider value={dispatch}>{children}</DialogUpdaterContext.Provider>
    </DialogContext.Provider>
  );
});

export const useDialogState = () => {
  const dialogState = useContext(DialogContext);
  if (typeof dialogState === "undefined") {
    throw new Error("useDialogState must be used within a DialogModalProvider");
  }
  return dialogState;
};

export const useDialogDispatch = () => {
  const dialogDispatch = useContext(DialogUpdaterContext);
  if (typeof dialogDispatch === "undefined") {
    throw new Error("useDialogDispatch must be used within a DialogModalProvider");
  }
  return dialogDispatch;
};
